import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { isAdminRole } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClassPicker } from "@/components/ClassPicker";
import { toast } from "sonner";
import { Plus, Send, Users, ArrowLeft, Eye, UserPlus, Trash2, Check, CheckCheck } from "lucide-react";

const time = (d) => new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export function Bubble({ m, mine, showSender, readLabel }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "btn-brand rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
        {showSender && <div className={`text-[11px] font-semibold mb-0.5 ${mine ? "text-white/80" : "text-violet-700"}`}>{m.from_name} • {ROLE_LABELS[m.from_role] || m.from_role}</div>}
        <div className="whitespace-pre-line">{m.body}</div>
        <div className={`text-[10px] mt-0.5 flex items-center gap-1 ${mine ? "text-white/70 justify-end" : "text-slate-400"}`}>
          {time(m.created_at)} {readLabel !== undefined && (readLabel ? <CheckCheck size={12} /> : <Check size={12} />)}
        </div>
      </div>
    </div>
  );
}

function MemberPicker({ candidates, selected, onToggle }) {
  const [q, setQ] = useState("");
  const shown = candidates.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <Input data-testid="group-member-search" placeholder="Search people" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="max-h-56 overflow-y-auto mt-2 divide-y divide-slate-100 border rounded-lg scrollbar-thin">
        {shown.map((c) => (
          <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer">
            <Checkbox data-testid={`group-member-${c.id}`} checked={selected.includes(c.id)} onCheckedChange={() => onToggle(c.id)} />
            <span className="flex-1">{c.name}</span><span className="text-xs text-slate-500">{ROLE_LABELS[c.role]}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function NewGroupDialog({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [scope, setScope] = useState("school");
  const [cls, setCls] = useState({ branch_id: user?.branch_id || "", class_name: "", section: "A" });
  const [candidates, setCandidates] = useState([]);
  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/chat-groups/candidates", { params: isAdminRole(user?.role) && cls.branch_id ? { branch_id: cls.branch_id } : {} }).then((r) => setCandidates(r.data)).catch(() => {});
  }, [open, cls.branch_id, user?.role]);
  const toggle = (id) => setMembers((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  async function create() {
    try {
      await api.post("/chat-groups", { name, scope, member_ids: members, branch_id: cls.branch_id || null, class_name: cls.class_name, section: cls.section });
      toast.success("Group created"); setName(""); setMembers([]); onCreated(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">New group chat</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Group name</Label><Input data-testid="group-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="All teachers" /></div>
            <div><Label>Type</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="group-scope" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="school">School-wide</SelectItem><SelectItem value="class">Class group</SelectItem></SelectContent>
              </Select></div>
          </div>
          {(scope === "class" || isAdminRole(user?.role)) && <ClassPicker value={cls} onChange={setCls} />}
          <MemberPicker candidates={candidates} selected={members} onToggle={toggle} />
          <Button data-testid="group-create" onClick={create} disabled={!name || (scope === "class" && !cls.class_name)} className="w-full btn-brand">Create group ({members.length} members)</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GroupChat({ group, onBack, onChanged }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [picked, setPicked] = useState([]);
  const endRef = useRef(null);
  const manage = isAdminRole(user?.role) || ["principal", "center_head"].includes(user?.role);

  useEffect(() => {
    const load = () => api.get(`/chat-groups/${group.id}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
    load();
    api.get(`/chat-groups/${group.id}/members`).then((r) => setMembers(r.data)).catch(() => {});
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [group.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    try { const { data } = await api.post(`/chat-groups/${group.id}/messages`, { body: text }); setMsgs((m) => [...m, data]); setText(""); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  async function openAdd() {
    const { data } = await api.get("/chat-groups/candidates", { params: group.branch_id ? { branch_id: group.branch_id } : {} });
    setCandidates(data.filter((c) => !group.member_ids.includes(c.id))); setPicked([]); setAdding(true);
  }
  async function saveAdd() {
    try { await api.patch(`/chat-groups/${group.id}/members`, { add: picked }); toast.success("Members added"); setAdding(false); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function removeGroup() {
    if (!window.confirm("Delete this group and all its messages?")) return;
    await api.delete(`/chat-groups/${group.id}`); onChanged(); onBack();
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <button data-testid="group-back" className="md:hidden text-slate-500" onClick={onBack}><ArrowLeft size={18} /></button>
        <div className="flex-1 min-w-0">
          <div data-testid="group-chat-name" className="font-semibold text-slate-900 truncate">{group.name}</div>
          <div className="text-xs text-slate-500 truncate">{group.scope === "class" ? `${group.class_name} ${group.section} • ` : "School-wide • "}{members.map((m) => m.name).join(", ")}</div>
        </div>
        {manage && <Button data-testid="group-add-members" size="sm" variant="outline" onClick={openAdd}><UserPlus size={14} /></Button>}
        {manage && <Button data-testid="group-delete" size="sm" variant="outline" onClick={removeGroup} className="text-rose-600"><Trash2 size={14} /></Button>}
      </div>
      {!group.is_member && <div className="px-4 py-2 text-xs bg-amber-50 text-amber-800 flex items-center gap-1"><Eye size={12} /> Monitoring only — you are not a member of this group</div>}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[55vh] scrollbar-thin">
        {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.from_id === user.id} showSender={m.from_id !== user.id} />)}
        <div ref={endRef} />
      </div>
      {group.is_member && (
        <form onSubmit={send} className="p-3 border-t border-slate-100 flex gap-2">
          <Input data-testid="group-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Message the group" />
          <Button data-testid="group-chat-send" type="submit" className="btn-brand"><Send size={16} /></Button>
        </form>
      )}
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Add members</DialogTitle></DialogHeader>
          <MemberPicker candidates={candidates} selected={picked} onToggle={(id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))} />
          <Button data-testid="group-add-save" onClick={saveAdd} disabled={!picked.length} className="btn-brand">Add {picked.length}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function GroupsPanel() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const manage = isAdminRole(user?.role) || ["principal", "center_head"].includes(user?.role);
  const load = () => api.get("/chat-groups").then((r) => { setGroups(r.data); setActive((a) => (a ? r.data.find((g) => g.id === a.id) || null : a)); }).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="grid md:grid-cols-[320px_1fr] gap-4 min-h-[60vh]">
      <div className={`bg-white rounded-2xl shadow-sm overflow-hidden ${active ? "hidden md:block" : ""}`}>
        {manage && <div className="p-3 border-b border-slate-100"><Button data-testid="new-group-btn" onClick={() => setCreating(true)} className="w-full btn-brand"><Plus size={15} className="mr-1" /> New group</Button></div>}
        <div className="divide-y divide-slate-100">
          {groups.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">No groups yet.</p>}
          {groups.map((g) => (
            <button key={g.id} data-testid={`group-${g.id}`} onClick={() => setActive(g)} className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-violet-50/60 ${active?.id === g.id ? "bg-violet-50" : ""}`}>
              <div className="h-10 w-10 rounded-full brand-header text-white flex items-center justify-center shrink-0"><Users size={18} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between"><span className="font-semibold text-slate-900 truncate">{g.name}</span>{g.unread > 0 && <span className="text-[10px] bg-rose-500 text-white rounded-full px-1.5 py-0.5">{g.unread}</span>}</div>
                <div className="text-xs text-slate-500">{g.scope === "class" ? `${g.class_name} ${g.section}` : "School-wide"} • {g.member_ids.length} members{!g.is_member ? " • monitoring" : ""}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className={`bg-white rounded-2xl shadow-sm ${active ? "" : "hidden md:flex items-center justify-center text-slate-400"}`}>
        {active ? <GroupChat key={active.id} group={active} onBack={() => setActive(null)} onChanged={load} /> : <p className="text-sm">Pick a group</p>}
      </div>
      <NewGroupDialog open={creating} onClose={() => setCreating(false)} onCreated={load} />
    </div>
  );
}

export function MonitorPanel() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { api.get("/messages/monitor/threads").then((r) => setThreads(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (active) api.get("/messages/monitor/conversation", { params: { a: active.a, b: active.b } }).then((r) => setMsgs(r.data)).catch(() => {});
  }, [active]);

  return (
    <div className="grid md:grid-cols-[340px_1fr] gap-4 min-h-[60vh]">
      <div className={`bg-white rounded-2xl shadow-sm overflow-hidden ${active ? "hidden md:block" : ""}`}>
        <div className="divide-y divide-slate-100">
          {threads.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">No conversations to monitor.</p>}
          {threads.map((t) => (
            <button key={`${t.a}-${t.b}`} data-testid={`monitor-thread-${t.a}-${t.b}`} onClick={() => setActive(t)} className={`w-full text-left px-4 py-3 hover:bg-violet-50/60 ${active === t ? "bg-violet-50" : ""}`}>
              <div className="font-semibold text-slate-900 text-sm">{t.a_name} <span className="text-slate-400">↔</span> {t.b_name}</div>
              <div className="text-[11px] text-slate-500">{ROLE_LABELS[t.a_role]} • {ROLE_LABELS[t.b_role]} • {t.count} messages</div>
              <div className="text-xs text-slate-500 truncate mt-0.5">{t.last.body}</div>
            </button>
          ))}
        </div>
      </div>
      <div className={`bg-white rounded-2xl shadow-sm flex flex-col ${active ? "" : "hidden md:flex items-center justify-center text-slate-400"}`}>
        {!active ? <p className="text-sm">Pick a conversation to review</p> : (
          <>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
              <button className="md:hidden text-slate-500" onClick={() => setActive(null)}><ArrowLeft size={18} /></button>
              <div className="font-semibold text-slate-900">{active.a_name} ↔ {active.b_name}</div>
              <span className="ml-auto text-xs text-amber-700 flex items-center gap-1"><Eye size={12} /> Read-only</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[55vh] scrollbar-thin">
              {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.from_id === active.a} showSender readLabel={m.read} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
